/** @format */

import {
  AnalyticsService,
  CookieService,
  EmitEvent,
  EventBusService,
  Events,
  Store,
  StoreService,
  UtilityService,
  WINDOW,
} from '@shared/data-access/services';
import {
  AvailabilityByLocationResponse,
  AvailabilityByLocationResult,
  Chain,
  IStoreLayerMessage,
  SkuStoreResult,
  StoreHours,
  StoreLayerItem,
  StoreLayerStoreResult,
  StoresByLocationResult,
  StoresByLocationWithMeta,
} from '@shared/data-access/interfaces';
import { CHAIN_NAME_ABBR_MAP, DCSG_CONSTANTS } from '@shared/data-access/constants';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { INVENTORY_TYPE, SEARCH_LOCATION, StoreStatus } from '@shared/data-access/enums';
import { finalize, map, takeUntil, timeout } from 'rxjs/operators';

import { AccordionComponent } from '../accordion/accordion.component';
import { FilterStoreResult } from './filter-store-result.interface';
import { Origin } from './origin.interface';
import { SideNavComponent } from '../side-nav/side-nav.component';
import { Subject } from 'rxjs';

@Component({
  selector: 'shared-components-store-layer',
  templateUrl: './store-layer.component.html',
  styleUrl: './store-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StoreLayerComponent implements OnInit, OnChanges {
  /**
   * @description
   * Cross chain bopis feature flag
   */

  @Input()
  public crossChainBopis: boolean;

  /**
   * @description
   * Used for the analytic message determine who is making the search
   * EX:
   * PDP
   * CART
   */
  @Input()
  public searchLocation: SEARCH_LOCATION;

  /**
   * @description
   * list of store layer items
   */
  @Input()
  public storeLayerItems: StoreLayerItem[];

  /**
   * @description
   * Flag to determine if the side panel is currently toggled
   */
  @Input()
  public isSidePanelToggled = false;

  /**
   * @description
   * Quantity selected by user
   */
  @Input()
  public quantity: string | number;

  /**
   * @description
   * Flag to determine if BOPIS is enabled
   */
  @Input()
  public bopisEnabled = true;

  /**
   * @description
   * Flag to determine if BOPL filter and messaging is enabled
   */
  @Input()
  public boplExperienceEnabled = false;

  @Input()
  public ecode = '';

  /**
   * @description
   * Text to display for changing store
   */
  @Input()
  public changeStoreText = 'Change Store';

  @Input()
  public storeName = '';

  /**
   * @description
   * EventEmitter for when a store is selected in side panel
   */
  @Output()
  public updateRadioGroup = new EventEmitter<object>();

  /**
   * @description
   * ViewChild reference of the SideNavComponent
   */
  @ViewChild('storeLayerNav', { static: false })
  public storeLayerNav: SideNavComponent;

  /**
   * @description
   * ViewChildren reference for store info accordion. Using these refs to close all other accordions after one is selected to be opened
   */
  @ViewChildren(AccordionComponent) accordionComponents!: QueryList<AccordionComponent>;

  protected readonly platformId = inject(PLATFORM_ID);
  protected readonly window = inject(WINDOW);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly storeService = inject(StoreService);
  private readonly store = inject(Store);
  private readonly utilityService = inject(UtilityService);
  private readonly cookieService = inject(CookieService);
  private readonly eventBusService = inject(EventBusService);
  private readonly chain = inject(Chain);

  /**
   * @description
   * The chain identifier abbreviation
   */
  public chainIdentifierAbbr = this.chain.chainMarketingIdentifierAbbr;

  /**
   * @description
   * The side panel heading
   */
  public heading = 'Select Store';

  /**
   * @description
   * Boolean to check if Show Stores w/ Availability is checked
   */
  public isAvailabilitySelected = true;

  /**
   * @description
   * Boolean to check if pickup today is checked
   */
  public isPickupTodaySelected = false;

  /**
   * @description
   * Flag for determining if a store is selected
   */
  public isStoreSelected = false;

  /**
   * @description
   * if true something is loading on the page
   */
  public loading = false;

  /**
   * @description
   * The index of the next store
   */
  public nextStoreIndex: number | null = null;

  /**
   * @description
   * The origin
   */
  public origin: Origin;

  /**
   * @description
   * Flag to determine if the product is buyable
   */
  public isBuyable = true;

  /**
   * @description
   * Boolean to check if user has clicked the searchIcon in zip code input
   */
  public searchClicked = false;

  /**
   * @description
   * If there was an error
   */
  public searchError = false;

  /**
   * @description
   * Message to user if zip code error occurs
   */
  public searchErrorMessage = '';

  /**
   * @description
   * Store Selected by user
   * TODO: Rename?
   */
  public selectedStoreId: any;

  /**
   * @description
   * Index of the selected store in the search results array
   * TODO: Rename?
   */
  public selectedStoreIndex: number | null = null;

  /**
   * @description
   * Default number of stores to display
   */
  public storeDisplay = DCSG_CONSTANTS.defaultNumberOfStores;

  /**
   * @description
   * Object to store the storeResults returned by API
   */
  public storeResults: StoreLayerStoreResult[] | undefined;

  /**
   * @description
   * Default store search radius
   */
  public storeSearchRadius = DCSG_CONSTANTS.defaultStoreSearchRadius;

  /**
   * @description
   * If user is using current geolocation
   * TODO: rename to usersCurrentLocation
   */
  public usingCurrentLocation = false;

  /**
   * @description
   * The ZipCode/Address entered by User
   * TODO: rename to enteredZipCode or userEnteredZipCode
   */
  public zipCode: any;
  /**
   * @description
   * returns store number as the key and the value of ATS
   * or ISA respectively
   */
  public lowestStoreResultATS: { [key: string]: number | undefined } = {};
  public lowestStoreResultISA: { [key: string]: number | undefined } = {};

  public skusArrayIdString = '';

  /**
   * @description
   * Subject used to cancel getCurrentGeolocationPosition subscription when getStoreDetails method is called
   */
  private destroyGetCurrentLocation$: Subject<void> = new Subject<void>();

  /**
   * @description
   * Used to determine to look at ats or isa for inventory
   */
  private inventoryType: INVENTORY_TYPE;

  public displayHomr: boolean;

  private clonedResults: AvailabilityByLocationResponse | StoresByLocationWithMeta;

  public currentResolvedSku: StoreLayerItem;

  private selectedCount: number;

  public checkboxIdRandom: string;

  public ngOnInit() {
    const possibleSku = this.storeService.findSelectedSku(this.storeLayerItems);

    const skuSelected = possibleSku === undefined ? undefined : possibleSku[0];

    const fromCart = this.searchLocation === SEARCH_LOCATION.CART;
    // check if a sku is selected.  No Sku selected means ecode level search
    if (skuSelected) {
      this.skusIdentifier([skuSelected]);
      this.displayHomr = skuSelected.displayHomr;
      this.inventoryType = this.storeService.findInventoryType(
        skuSelected.isaFlag,
        skuSelected.bopisable,
        fromCart,
      );
    } else {
      this.skusIdentifier(this.storeLayerItems);
      const item = this.storeLayerItems === undefined ? undefined : this.storeLayerItems[0];
      this.displayHomr =
        this.storeLayerItems === undefined ? undefined : this.storeLayerItems[0].displayHomr;
      const isaFlag = item === undefined ? true : item.isaFlag;
      const atsFlag = item === undefined ? true : item.bopisable;
      this.inventoryType = this.storeService.findInventoryType(isaFlag, atsFlag, fromCart);
    }

    this.quantity = this.quantity === undefined ? 1 : this.quantity;

    // allows for multiple instances of store layer checkbox for show unavailable
    this.checkboxIdRandom = `showStoresCheckbox_${(Math.random() * 1000 + 1)
      .toString()
      .substring(0, 3)}`;
    this.eventBusService.on(Events.ChangeStore, () => {
      this.isSidePanelToggled = !this.isSidePanelToggled;
    });

    this.subscribeToStore();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const fromCart = this.searchLocation === SEARCH_LOCATION.CART;

    this.quantity = this.quantity === undefined ? 1 : this.quantity;

    const temp = this.selectedCount;

    const selectedSkus = this.storeService.findSelectedSku(this.storeLayerItems);
    this.selectedCount = selectedSkus?.length;
    const didNumberOfSkusChange =
      temp !== this.selectedCount &&
      this.selectedCount !== 0 &&
      this.searchLocation === SEARCH_LOCATION.BUNDLE;

    // Determine if sku has changed (not a bundle page)
    let skuHasChanged = false;
    let isFirstSkuResolve = false;
    const tempResolvedSku: StoreLayerItem = selectedSkus?.[0];

    if (this.currentResolvedSku === undefined && tempResolvedSku !== undefined) {
      skuHasChanged = true;
      isFirstSkuResolve = true;
      this.currentResolvedSku = tempResolvedSku;
    }
    if (!isFirstSkuResolve && this.currentResolvedSku?.id !== tempResolvedSku?.id) {
      skuHasChanged = true;
      this.currentResolvedSku = tempResolvedSku;
    }
    // bundle product checks
    // skuHasChanged should update when only 1 product is selected
    // didNumberOfSkusChange should update when we have multiple selected (Bundle page)
    if (skuHasChanged || didNumberOfSkusChange) {
      this.isBuyable = this.areItemsBuyable();
      let atsFlag = this.findFalseBopisFlags(selectedSkus);
      // if flag is undefined we did not find a false flag. use any items bopis flag
      atsFlag = atsFlag === undefined ? this.currentResolvedSku?.bopisable : atsFlag;
      let isaFlag = this.findFalseIsaFlags(selectedSkus);
      isaFlag = isaFlag === undefined ? this.currentResolvedSku?.isaFlag : isaFlag;
      this.inventoryType = this.storeService.findInventoryType(isaFlag, atsFlag, fromCart);

      // Determine if the availability call should be made
      const didASkuResolve = isFirstSkuResolve || didNumberOfSkusChange;
      setTimeout(() => {
        // had to add the timeout bc i kept getting expresion changed after it has been checked
        this.onChangesCall(
          this.currentResolvedSku,
          this.didQtyChange(changes),
          skuHasChanged,
          didASkuResolve,
        );
      }, 0);
    }
  }

  /**
   * @description
   * Will return bopis flag if a false bopis item is found
   */
  public findFalseBopisFlags(selected: StoreLayerItem[]) {
    if (selected === undefined) {
      return undefined;
    }

    if (selected?.length === 0) {
      return undefined;
    }
    const foundBopisFalseList = selected.find((item) => !item.bopisable);

    return foundBopisFalseList === undefined ? undefined : foundBopisFalseList.bopisable;
  }

  /**
   * @description
   * Will return bopis flag if a false bopis item is found
   */
  public findFalseIsaFlags(selected: StoreLayerItem[]) {
    if (selected === undefined) {
      return undefined;
    }

    if (selected?.length === 0) {
      return undefined;
    }
    const foundIsaFlags = selected.find((item) => !item.selectedSku);
    return foundIsaFlags === undefined ? undefined : foundIsaFlags.isaFlag;
  }

  /**
   * @description
   * Determines if qty has changed
   * @param changes
   */
  public didQtyChange(changes: SimpleChanges) {
    const validQuantityChange =
      changes?.quantity?.currentValue !== changes?.quantity?.previousValue;
    const isFirstChange = changes?.quantity?.firstChange;
    return !isFirstChange && validQuantityChange;
  }

  public onChangesCall(
    item: any,
    didQtyChange: boolean,
    didSkuChange: boolean,
    isFirstSkuChange: boolean,
  ) {
    // if zip code or sku changed filter the list again
    if (isFirstSkuChange || didSkuChange || didQtyChange) {
      this.getStoreDetails(
        this.zipCode,
        this.getSkuIdentifier,
        this.quantity,
        this.isBuyable,
        isFirstSkuChange,
        false,
      );
    }
  }

  private subscribeToStore() {
    this.store.selectDistinctKey('CUST_STORE').subscribe((store) => {
      if (typeof store === 'string') {
        // Get the store ID
        const tempStoreId = store.toString().split('_')[2];

        // Check to see if the store ID has changed
        if (this.selectedStoreId !== tempStoreId) {
          this.selectedStoreId = tempStoreId;

          this.isStoreSelected = true;

          // default to selected zip if it exists
          // this may be in this format 15301-2701
          const dirtyZipcode = store.toString().split('_')[0];

          if (dirtyZipcode.length > 5) {
            this.zipCode = dirtyZipcode.substr(0, 5);
          } else {
            this.zipCode = dirtyZipcode;
          }

          // Put this into a new thread so that it doesn't interrupt existing change detection cycle
          setTimeout(() => {
            let skuSelected;
            if (this.storeLayerItems !== undefined) {
              skuSelected = this.storeService.findSelectedSku(this.storeLayerItems)[0];
            }

            // no sku selected call at ecode level
            if (skuSelected === undefined) {
              this.getStoreDetails(
                this.zipCode,
                undefined,
                this.quantity,
                this.isBuyable,
                true,
                false,
              );
            } else {
              this.getStoreDetails(
                this.zipCode,
                this.getSkuIdentifier,
                this.quantity,
                this.isBuyable,
                true,
                false,
              );
            }
          }, 0);
        }
      }
    });
  }

  /**
   * @description
   * Function to get list of stores based on string entered by user
   * @param searchString
   * @param productSku
   * @param qty
   * @param isBuyable
   * @param freshSearch
   * @param didSearchFromStoreLayer
   */
  public getStoreDetails(
    searchString,
    productSku,
    qty,
    isBuyable,
    freshSearch: boolean,
    didSearchFromStoreLayer = false,
  ) {
    this.searchClicked = true;
    this.storeResults = undefined;
    this.nextStoreIndex = null;
    this.selectedStoreIndex = null;
    this.searchError = false;
    this.destroyGetCurrentLocation$.next();
    this.destroyGetCurrentLocation$.complete();
    // check if input is ready to search
    if (
      searchString &&
      (this.usingCurrentLocation || (!this.usingCurrentLocation && searchString.match(/^\d{5}$/)))
    ) {
      this.loading = true;

      // only call the api if we need to
      // If we do not need the api use the cloned results
      if (freshSearch) {
        // calls the api
        this.fetchStoreInformation(
          searchString,
          productSku,
          qty,
          this.isAvailabilitySelected,
          didSearchFromStoreLayer,
        );
      } else {
        // filters the cloned list no api call
        this.processApiResponse(this.clonedResults, productSku, this.isAvailabilitySelected);
      }
    } else {
      // Set the search error
      this.setSearchError('Please enter a zip code.');
    }
    this.usingCurrentLocation = false;
  }

  /**
   * @description
   * Fetchs store information from the store service
   * @param searchString
   * @param productSku
   * @param qty
   * @param isAvailabilitySelected
   * @param didSearchFromStoreLayer
   */
  public fetchStoreInformation(
    searchString,
    productSku,
    qty,
    isAvailabilitySelected: boolean,
    didSearchFromStoreLayer: boolean,
  ) {
    this.storeService
      .getStoreInformation$(searchString, productSku, 1)
      .pipe(
        map((res) => this.parseStoreResults(res)),
        finalize(() => {
          // only fire if they click do not fire on auto search
          if (didSearchFromStoreLayer) {
            // Trigger the CSA Event
            this.fireAnalyticEvent(didSearchFromStoreLayer, searchString, productSku, qty);
          }
        }),
      )
      .subscribe(
        (response) => {
          // copy list to use again
          this.clonedResults = response;

          // Set loading to false
          this.loading = false;

          if (
            response !== undefined &&
            response !== null &&
            'meta' in response &&
            response.meta?.[0].key !== undefined &&
            response.meta?.[0].key !== null &&
            response.meta?.[0].key !== 1
          ) {
            // Set the search error
            this.setSearchError(this.determineSearchErrorMessage(response, productSku));
          } else {
            this.processApiResponse(response, productSku, isAvailabilitySelected);
          }
        },
        () => {
          // Set loading to false
          this.loading = false;

          // Set the search error
          this.setSearchError('There was an error getting nearby stores.  Please try again.');
          this.fireAnalyticEvent(didSearchFromStoreLayer, searchString, productSku, qty);
        },
      );
  }

  /**
   * @description
   * Method that will process the response if it comes from the api or from memory
   * @param response
   * @param productSku
   * @param isAvailabilitySelected
   */
  public processApiResponse(
    response: AvailabilityByLocationResponse | StoresByLocationWithMeta,
    productSku: string | any,
    isAvailabilitySelected: boolean,
  ) {
    this.loading = false;
    // Iterate through the results
    this.filterList(response.data.results, this.inventoryType, isAvailabilitySelected);

    // if chain is cross chain bopis set log
    if (this.crossChainBopis) {
      this.setCrossChainLogo(this.storeResults);
    }

    // Check to see if we have the origin address
    if (
      response.data.origin != null &&
      typeof response.data.origin.geocoded_address === 'string' &&
      response.data.origin.geocoded_address.trim() !== ''
    ) {
      this.populateOriginInterface(response);
      this.zipCode = response.data.origin.geocoded_address.match(/\d{5}/)[0];
    }

    // formats the store name, open, close times
    this.prepStoreData();

    this.generateSearchRadiusErrorMessage(productSku, this.storeResults.length);
  }

  /**
   * @description
   * Sets the logo svg to the correct store
   * @param storeData
   */
  public setCrossChainLogo(storeData: StoreLayerStoreResult[] | undefined) {
    if (storeData !== undefined) {
      for (const storeResult of storeData) {
        // remove the spaces in the store name
        const storeNameWithNoSpaces = UtilityService.cleanString(
          storeResult.store.chain.toLowerCase(),
          '\\s',
        );
        if (storeNameWithNoSpaces === this.chain.chainIdentifier.toLowerCase()) {
          storeResult.store['logo'] = `assets/image/${this.chain.logoSVG}`;
        } else {
          const storeIdentifierAbbr = Object.hasOwn(CHAIN_NAME_ABBR_MAP, storeNameWithNoSpaces)
            ? CHAIN_NAME_ABBR_MAP[storeNameWithNoSpaces as keyof typeof CHAIN_NAME_ABBR_MAP]
            : undefined;
          if (
            storeIdentifierAbbr &&
            this.chain.crossChainBopisPartners?.includes(storeIdentifierAbbr)
          ) {
            const logoPath =
              typeof this.chain.crossChainLogoSVG === 'string'
                ? this.chain.crossChainLogoSVG
                : this.chain.crossChainLogoSVG?.[storeNameWithNoSpaces];
            const logoURL = `assets/image/${logoPath}`;
            storeResult.store['logo'] = logoURL;
          }
        }

        // set class for the style heading
        storeResult.store['titleStyle'] = this.fetchCrossChainTitleStyle(storeNameWithNoSpaces);
      }
    }
  }

  /**
   * @description
   * Returns the title style required depending on the store
   *
   * @param store
   */
  public fetchCrossChainTitleStyle(store: string) {
    if (store.toLowerCase() === 'dickssportinggoods') {
      return 'cross-chain-dsg-title-color';
    } else if (store.toLowerCase() === 'golfgalaxy') {
      return 'cross-chain-gg-title-color';
    } else if (store.toLowerCase() === 'publiclands') {
      return 'cross-chain-pl-title-color';
    } else if (store.toLowerCase() === 'moosejaw') {
      return 'cross-chain-mj-title-color';
    }
    return '';
  }

  /**
   * @description
   * Filters the store results and fires an StoreSearchResult event
   * @param storeSearchResults
   * @param inventoryType
   * @param isAvailabilitySelected
   */
  private filterList(
    storeSearchResults: AvailabilityByLocationResult[] | StoresByLocationResult[],
    inventoryType: INVENTORY_TYPE,
    isAvailabilitySelected: boolean,
  ) {
    // remove or keep the required stores
    const filteredInterface = this.filterStores<
      AvailabilityByLocationResult | StoresByLocationResult
    >(storeSearchResults, inventoryType, isAvailabilitySelected);

    const filteredList = filteredInterface?.filteredList;
    // set filtered list
    this.storeResults =
      filteredList.length === 0 && !filteredInterface.displayEmptyFilterList
        ? storeSearchResults
        : filteredList;
  }

  public checkForAnyInventory(list): StoreLayerItem[] | any {
    if (list === undefined) {
      return undefined;
    }
    return list.filter((item) => item.qty.ats > 0 || item.qty.isa > 0 || item.qty.bopl > 0);
  }

  /**
   * TODO: We could move this to its own service
   * Filters the stores out based on the inventory type and if availability check box is clicked
   * @param storeSearchResults
   * @param inventoryType
   * @param isAvailabilitySelected
   */
  public filterStores<T extends AvailabilityByLocationResult | StoresByLocationResult>(
    storeSearchResults: T[],
    inventoryType: INVENTORY_TYPE,
    isAvailabilitySelected: boolean,
  ): FilterStoreResult<T> {
    // Number of stores with inventory for SKU
    let numberOfStoresWithInventory = 0;

    // Flag to determine if currently set store has availability
    let doesCurrentlySetStoreHaveInventory = false;

    // list to hold filter results
    const filteredStoreList: T[] = [];

    const selectedSku = this.storeService.isSkuSelected(this.storeLayerItems)
      ? this.storeService.findSelectedSku(this.storeLayerItems)
      : undefined;

    // Ensure there is something to iterate
    if (Array.isArray(storeSearchResults) && storeSearchResults?.length > 0) {
      // Iterate the stores
      for (let i = 0, length = storeSearchResults?.length; i < length; i += 1) {
        const tempStore = storeSearchResults[i];

        // create a list all sku ids
        const skuIds = this.storeService.convertDataIntoList(this.storeLayerItems);
        // find all selected skus
        const isSkuInStore = this.storeService.isSkuInStore(
          'skus' in tempStore ? tempStore.skus : undefined,
          skuIds,
        );
        // find all skus with some sort of inventory
        const selectedSkusWithInvntory = this.checkForAnyInventory(isSkuInStore);

        // make we have same amount of sku ids and skus with inventory at a store
        // this is in place for bundles a store needs both skus to pick up
        if (selectedSkusWithInvntory?.length === skuIds?.length) {
          this.lowestStoreResultATS[tempStore.store.location] = Number.MAX_SAFE_INTEGER;
          this.lowestStoreResultISA[tempStore.store.location] = Number.MAX_SAFE_INTEGER;

          const skus = isSkuInStore[0];
          // Check to see if the current store has SKU inventory and is not the currently selected store
          if (typeof tempStore !== 'undefined' && skus !== undefined) {
            // Sets lowestStoreResultATS and lowestStoreResultISA
            this.setStoreResultsQuantities(tempStore.store.location, skus);

            // Checks if current store has inventory
            doesCurrentlySetStoreHaveInventory = this.checkDoesCurrentlySetStoreHaveInventory(
              this.lowestStoreResultATS[tempStore.store.location],
              tempStore,
            );

            // check if store is open before counting it
            const isStoreOpen = this.isStoreOpen(tempStore.store.status);

            if (isStoreOpen) {
              // if current store does not have inventory for ATS check ISA inventory. (Its ok to return 0 if ISA does not have inventory at this point)
              let inventory =
                this.lowestStoreResultATS[tempStore.store.location] === 0
                  ? this.lowestStoreResultISA[tempStore.store.location]
                  : this.lowestStoreResultATS[tempStore.store.location];

              // if there is no ats or isa qty, use bopl qty
              if (inventory === 0) {
                inventory = this.utilityService.convertToNumber(skus.qty.bopl);
              }

              // Gets inventory count
              numberOfStoresWithInventory = this.incrementStoresWithInventoryCount(
                inventory,
                tempStore,
                numberOfStoresWithInventory,
              );
            }

            // user selected quantity
            const quantity = this.utilityService.convertToNumber(this.quantity);
            // get the current store inventory based off inventory type
            let currentStoreInventory = this.storeService.fetchInventoryFromStore(
              inventoryType,
              this.lowestStoreResultATS[tempStore.store.location],
              this.lowestStoreResultISA[tempStore.store.location],
              this.crossChainBopis,
            );

            // if pickup today filter is not selected, add bopl quantity to inventory considered when deciding if store should be shown in side panel
            if (
              !this.isPickupTodaySelected &&
              this.boplExperienceEnabled &&
              skus?.qty?.bopl &&
              (inventoryType === INVENTORY_TYPE.ATS ||
                inventoryType === INVENTORY_TYPE.BOTH ||
                (this.crossChainBopis && inventoryType === INVENTORY_TYPE.NONE))
            ) {
              currentStoreInventory += this.utilityService.convertToNumber(skus.qty.bopl);
            }

            // add to filter list if availability check box is selected selected and we should add store based off currentStoreInventory, the sku inventory type, and user selected quantity
            if (
              (isAvailabilitySelected || this.isPickupTodaySelected) &&
              this.shouldAddStoreBasedOnInventory(inventoryType, currentStoreInventory, quantity)
            ) {
              // The saved store has BOPIS inventory, increment the store counter
              filteredStoreList.push(tempStore);
            }
          }
        }
      }
    }

    // check if we should just display the empty list
    const shouldDisplayEmptyFilterList = this.shouldDisplayEmptyFilterList(
      inventoryType,
      isAvailabilitySelected,
      filteredStoreList.length,
      selectedSku === undefined,
    );

    // return the interface for client to use
    return {
      filteredList: filteredStoreList,
      doesCurrentlySetStoreHaveInventory,
      storesWithInventoryCount: numberOfStoresWithInventory,
      // if inventory type is NONE display an empty list
      displayEmptyFilterList: shouldDisplayEmptyFilterList,
    };
  }

  /**
   * @description
   * Determines if we should add the store based of inventory availablity
   * @param inventoryType
   * @param inventory
   * @param quantity
   */
  public shouldAddStoreBasedOnInventory(
    inventoryType: INVENTORY_TYPE,
    inventory: number | any,
    quantity: number,
  ) {
    return (
      (this.crossChainBopis && inventoryType === INVENTORY_TYPE.NONE && inventory > 0) ||
      (inventoryType === INVENTORY_TYPE.BOTH && inventory > 0) ||
      (inventoryType === INVENTORY_TYPE.ISA && inventory >= quantity) ||
      (inventoryType === INVENTORY_TYPE.ATS && inventory >= quantity)
    );
  }

  /**
   * @description
   * Determines when we should display just the empty list
   * @param inventoryType
   * @param isAvailabilitySelected
   * @param filterListLength
   * @param areSkusUndefinedInResponse (no skus in the response indicates a not sku resolved search)
   */
  public shouldDisplayEmptyFilterList(
    inventoryType: INVENTORY_TYPE,
    isAvailabilitySelected: boolean,
    filterListLength: number,
    areSkusUndefinedInResponse: boolean,
  ) {
    if (areSkusUndefinedInResponse) {
      return false;
    }

    if (inventoryType === INVENTORY_TYPE.NONE && isAvailabilitySelected) {
      return true;
    }
    if (inventoryType !== INVENTORY_TYPE.NONE && filterListLength === 0 && isAvailabilitySelected) {
      return true;
    }
    return false;
  }

  public checkDoesCurrentlySetStoreHaveInventory(
    skuInventory: string | number | undefined,
    tempStore: SkuStoreResult,
  ) {
    // Check the SKU atsqty
    const quantity = this.utilityService.convertToNumber(this.quantity);
    // as of typscript v5, Operator '>=' cannot be applied to types 'string | number' and 'number'.
    if (typeof skuInventory === 'string') {
      skuInventory = parseInt(skuInventory);
    }
    return (
      skuInventory !== undefined &&
      skuInventory >= quantity &&
      tempStore.store.location === `${this.selectedStoreId}`
    );
  }

  public incrementStoresWithInventoryCount(
    skuInventory: string | number | undefined,
    tempStore: SkuStoreResult,
    storesWithInventoryCount: number,
  ) {
    const quantity = this.utilityService.convertToNumber(this.quantity);
    if (typeof skuInventory === 'string') {
      skuInventory = parseInt(skuInventory);
    }
    if (
      skuInventory !== undefined &&
      skuInventory >= quantity &&
      tempStore.store.location !== this.selectedStoreId
    ) {
      // The current store has BOPIS inventory, increment the store counter
      return (storesWithInventoryCount += 1);
    }
    return storesWithInventoryCount;
  }

  /**
   * @description
   * Iterates over the returned store data from api to prep data for ui
   */
  public prepStoreData() {
    // Reset store indexes to null
    this.selectedStoreIndex = null;
    this.nextStoreIndex = null;

    for (let i = 0; i < this.storeResults.length; i++) {
      const store = this.storeResults[i];

      // format store hours
      this.formatStoreTimes(store, 'storeHours');

      // format curbside hours
      this.formatStoreTimes(store, 'curbsideHours');

      // Check to see if the store is selected
      store.selectedStore = this.selectedStoreId === store.store.location;

      // If there is already a selected store then set its index
      if (store.selectedStore) {
        this.selectedStoreIndex = i;
        this.nextStoreIndex = i;
      }
    }
  }

  /**
   * @description
   * Populates the origin Interface data
   * @param response
   */
  public populateOriginInterface(
    response: AvailabilityByLocationResponse | StoresByLocationWithMeta,
  ) {
    // Get the origin address and trim any extra whitespace
    const originAddress = response.data.origin.geocoded_address.replace(/  +/g, ' ').trim();

    this.origin = {};

    // Get the origin city
    this.origin.originCity = originAddress.substring(0, originAddress.indexOf(','));

    // Substring out the origin city, remove commas and extra whitespace, and split by space
    const locationList = originAddress
      .substring(originAddress.indexOf(','))
      .replace(/,/g, '')
      .trim()
      .split(' ');

    // Check to ensure origin state is defined
    if (typeof locationList[0] !== 'undefined') {
      this.origin.originState = locationList[0];
    }

    // Check to ensure origin zip is defined
    if (typeof locationList[1] !== 'undefined') {
      this.origin.originZip = locationList[1];
    }

    // Check to ensure origin country is defined
    if (typeof locationList[2] !== 'undefined') {
      this.origin.originCountry = locationList[2];
    }
  }

  /**
   * @description
   * Determines messaging if no store with in range
   * @param response
   * @param productSku
   */
  public determineSearchErrorMessage(response, productSku): string {
    if (response.meta[0].key.toString() === '5000') {
      return 'Please enter a valid zip code.';
    } else if (response.meta[0].key.toString() === '5001') {
      if (productSku && this.isAvailabilitySelected) {
        return `Sorry, this product is not currently available at any of our stores within ${this.storeSearchRadius} miles of your location.`;
      } else {
        return `Sorry there are no stores within ${this.storeSearchRadius} miles of your location.`;
      }
    }
  }

  /**
   * @description
   * Formats the store times for the ui
   * @param store
   */
  public formatStoreTimes(
    store: StoreLayerStoreResult,
    storeHourType: 'storeHours' | 'curbsideHours',
  ) {
    if (this.utilityService.convertToNumber(store.distance) <= this.storeSearchRadius) {
      const hours = store.store[storeHourType];
      if (hours !== undefined) {
        store[`${storeHourType}closedAllDays`] = true;
        for (const [key, value] of Object.entries(hours)) {
          const timings = this.formatHours(value);
          store.store[storeHourType][key as keyof StoreHours] = timings;

          if (timings.toUpperCase() !== 'Closed'.toUpperCase()) {
            store[`${storeHourType}closedAllDays`] = false;
          }
          store.store.mapUrl = `https://maps.google.com/maps?saddr=My Location&daddr=${encodeURIComponent(
            store.store.chain,
          )},${encodeURIComponent(store.store.street2)},${encodeURIComponent(
            store.store.street1,
          )},${encodeURIComponent(store.store.zip)}`;

          store.showMore = false;
        }
      }
    }
  }

  public formatHours(value: any) {
    let timings;

    const timeSplitter = value.toString().split('-');

    let openingTime: any = parseInt(timeSplitter[0], 10);

    let closingTime: any = parseInt(timeSplitter[1], 10);

    if (openingTime < 100) {
      openingTime = `${
        parseInt(timeSplitter[0]?.substring(0, 2), 10) + 12
      }:${timeSplitter[0]?.substring(2, 4)} am`;
    } else if (openingTime < 1200) {
      openingTime = `${parseInt(
        timeSplitter[0]?.substring(0, 2),
        10,
      )}:${timeSplitter[0]?.substring(2, 4)} am`;
    } else if (openingTime < 1300) {
      openingTime = `${parseInt(
        timeSplitter[0]?.substring(0, 2),
        10,
      )}:${timeSplitter[0]?.substring(2, 4)} pm`;
    } else {
      openingTime = `${
        parseInt(timeSplitter[0]?.substring(0, 2), 10) - 12
      }:${timeSplitter[0]?.substring(2, 4)} pm`;
    }

    if (closingTime < 100) {
      closingTime = `${
        parseInt(timeSplitter[1]?.substring(0, 2), 10) + 12
      }:${timeSplitter[1]?.substring(2, 4)} am`;
    } else if (closingTime < 1200) {
      closingTime = `${parseInt(
        timeSplitter[1]?.substring(0, 2),
        10,
      )}:${timeSplitter[1]?.substring(2, 4)} am`;
    } else if (closingTime < 1300) {
      closingTime = `${parseInt(
        timeSplitter[1]?.substring(0, 2),
        10,
      )}:${timeSplitter[1]?.substring(2, 4)} pm`;
    } else {
      closingTime = `${
        parseInt(timeSplitter[1]?.substring(0, 2), 10) - 12
      }:${timeSplitter[1]?.substring(2, 4)} pm`;
    }

    // Store Closings
    if (openingTime === closingTime) {
      timings = 'Closed';
    } else {
      timings = openingTime.concat(' - ').concat(closingTime);
    }
    return timings;
  }

  /* Store Layer 3.0 Start */
  /**
   * @description
   * Function to set the store as well as update store Cookie
   */
  public setCustomerStore() {
    if (
      this.nextStoreIndex === null ||
      !this.storeResults?.length ||
      this.nextStoreIndex >= this.storeResults.length
    ) {
      return;
    }

    this.selectedStoreIndex = this.nextStoreIndex;
    const selectedStore = this.storeResults[this.selectedStoreIndex].store;

    if (this.searchLocation === SEARCH_LOCATION.CART) {
      this.eventBusService.emit(
        new EmitEvent(Events.SkuStoreSelected, {
          sku: this.storeLayerItems[0].id,
          store: selectedStore,
        }),
      );
    }

    this.setCookie(selectedStore);
    // Update the application store
    this.store.set(
      'CUST_STORE',
      `${selectedStore.zip}_${selectedStore.name}_${selectedStore.location}_${selectedStore.status}`,
    );
    this.storeLayerNav.panelToggle('close');
  }

  public selectStore(nextIndex: number) {
    const prevIndex = this.nextStoreIndex;
    if (typeof prevIndex === 'number') {
      this.storeResults[prevIndex].selectedStore = false;
    }

    this.storeResults[nextIndex].selectedStore = true;
    this.nextStoreIndex = nextIndex;
  }

  public storeInfoClicked(isCollapsed: boolean, selectedStoreInfoIndex: number) {
    if (!isCollapsed) {
      this.accordionComponents.map((accordion, index) => {
        if (index !== selectedStoreInfoIndex) {
          accordion.isCollapsed.set(true);
        }
      });
    }
  }
  /* Store Layer 3.0 End */

  /**
   * @description
   * Function to get the current location of user and calls EAT API
   *
   * @param productSku
   * @param qty
   * @param didSearchFromStoreLayer
   */
  public userCurrentLocation(productSku, qty, didSearchFromStoreLayer: boolean) {
    // Set loading to true
    this.loading = true;

    // Attempt to get the geolocation of the user
    this.utilityService
      .getCurrentGeolocationPosition()
      .pipe(timeout(15000), takeUntil(this.destroyGetCurrentLocation$))
      .subscribe(
        (position) => {
          // Check to see if position is available
          if (typeof position !== 'undefined') {
            this.usingCurrentLocation = true;

            const addr = `${position.coords.latitude},${position.coords.longitude}`;

            // Get the list of stores within the current position radius
            this.getStoreDetails(
              addr,
              productSku,
              qty,
              this.isBuyable,
              true,
              didSearchFromStoreLayer,
            );
          }
        },
        () => {
          // Set loading to false
          this.loading = false;

          // Set the search error
          this.setSearchError('Unable to get your current location, please enter a zip code.');
        },
      );
  }

  /**
   * @description
   * Determines if every sku layer items has buyable flag = true
   */
  public areItemsBuyable(): boolean {
    if (this.storeLayerItems === undefined) {
      return false;
    }
    return this.storeLayerItems.every((item: StoreLayerItem) => item.buyable);
  }

  public fireAnalyticEvent(didAutoSearch: any, searchString: any, productSku: any, qty: any) {
    // define object incase it was not created
    if (this.origin === undefined) {
      this.origin = {};
    }
    this.analyticsService.triggerAnalyticsEvent('CheckStoreAvailabilitySearch', {
      SearchAuto: `${didAutoSearch}`,
      SearchCity: this.origin.originCity || '',
      SearchCountry: this.origin.originCountry || '',
      SearchGeoLocate: `${typeof searchString === 'string' && searchString.indexOf(',') > -1}`,
      SearchLocation: this.searchLocation.toString(),
      SearchOnlyAvailableStores: `${this.isAvailabilitySelected}`,
      SearchProductID: this.ecode,
      SearchProductSKU: this.storeLayerItems === undefined ? '' : this.storeLayerItems[0].id,
      SearchQuantity: `${qty}`,
      SearchRadius: `${this.storeSearchRadius} Miles`,
      SearchState: this.origin.originState || '',
      SearchStatus:
        Array.isArray(this.storeResults) && this.storeResults.length > 0 ? 'Success' : 'Failure',
      SearchStoreList: this.storeResults,
      SearchTextString: searchString,
      SearchZip: this.origin.originZip || '',
    });
  }

  public generateSearchRadiusErrorMessage(productSku, listLength: number | string) {
    if (productSku && listLength === 0) {
      this.searchErrorMessage = `Sorry, this product is not currently available at any of our stores within ${this.storeSearchRadius} miles of your location.`;
      this.searchError = true;
    }
  }

  private parseStoreResults(res: AvailabilityByLocationResponse | StoresByLocationWithMeta) {
    const storeResults = res.data.results;
    const parsedResult: AvailabilityByLocationResult[] = [];
    let skusPresent = false;
    if (this.storeLayerItems?.length > 1) {
      for (const store of storeResults) {
        let skuMatch = 0;
        if ('skus' in store && store.skus !== undefined) {
          skusPresent = true;
          for (const storeSku of store.skus) {
            if (this.getSkuIdentifier?.includes(storeSku.sku)) {
              ++skuMatch;
            }
          }
          if (skuMatch === this.storeLayerItems.length) {
            parsedResult.push(store);
          }
        }
      }
      res.data.results = skusPresent ? parsedResult : storeResults;
    }

    return res;
  }

  private setStoreResultsQuantities(storeLocation: string, sku: any) {
    if (!sku?.qty?.ats) {
      this.lowestStoreResultATS[storeLocation] = undefined;
    } else {
      this.lowestStoreResultATS[storeLocation] =
        this.utilityService.convertToNumber(sku.qty.ats) < this.lowestStoreResultATS[storeLocation]
          ? this.utilityService.convertToNumber(sku.qty.ats)
          : this.lowestStoreResultATS[storeLocation];
    }

    if (!sku?.qty?.isa) {
      this.lowestStoreResultISA[storeLocation] = undefined;
    } else {
      this.lowestStoreResultISA[storeLocation] =
        this.utilityService.convertToNumber(sku.qty.isa) < this.lowestStoreResultISA[storeLocation]
          ? this.utilityService.convertToNumber(sku.qty.isa)
          : this.lowestStoreResultISA[storeLocation];
    }
  }

  /**
   * @description
   * Sets the loading flag to false and sets an error message to be displayed
   */
  private setSearchError(errorMessage: string) {
    // Set error fields
    this.searchErrorMessage = errorMessage;
    this.searchError = true;
  }

  /**
   * @description
   * Sets a cookie for cross chain
   * @param store
   */
  public setCookie(store) {
    const chainNameWithoutSpaces = UtilityService.cleanString(store.chain.toLowerCase(), '\\s');
    this.cookieService.set('CROSS_CHAIN_BOPIS', chainNameWithoutSpaces, undefined, '/');
  }

  /**
   * @description
   * Determines if curbside pickup is shown
   * @param storeResult
   * @param quantity
   */
  public canPickupCurbside(storeResult: StoreLayerStoreResult, quantity: string | number): boolean {
    const messageFlags = this.findBasicMessageFlags(storeResult);
    const isSkuInStore = messageFlags.skusInStores;

    if (
      !messageFlags.skuSelected ||
      messageFlags.isSkuFound === undefined ||
      !messageFlags.doAllSelectedSkusHaveInventory ||
      isSkuInStore?.[0] === undefined
    ) {
      return false;
    }
    const atsInventory =
      this.searchLocation === SEARCH_LOCATION.BUNDLE
        ? this.lowestStoreResultATS[storeResult.store.location]
        : this.utilityService.convertToNumber(isSkuInStore[0].qty.ats);

    return !!(
      (this.crossChainBopis || messageFlags.foundSkus?.[0]?.bopisable) &&
      atsInventory > 0 &&
      atsInventory >= this.utilityService.convertToNumber(quantity) &&
      this.bopisEnabled
    );
  }

  /**
   * @description
   * Determines if bopl is shown
   * @param storeResult
   * @param quantity
   */
  public canShipToStore(storeResult: StoreLayerStoreResult, quantity: string | number): boolean {
    const messageFlags = this.findBasicMessageFlags(storeResult);
    const skusInStore = messageFlags.skusInStores;

    if (
      !messageFlags.skuSelected ||
      messageFlags.isSkuFound === undefined ||
      !messageFlags.doAllSelectedSkusHaveInventory ||
      skusInStore?.[0] === undefined
    ) {
      return false;
    }

    const boplInventory = this.utilityService.convertToNumber(skusInStore[0].qty.bopl);
    return !!(
      (this.crossChainBopis || messageFlags.foundSkus?.[0]?.bopisable) &&
      boplInventory > 0 &&
      boplInventory >= this.utilityService.convertToNumber(quantity) &&
      this.boplExperienceEnabled
    );
  }

  /**
   * @description
   * Determines if available in store
   * @param storeResult
   * @param quantity
   */
  public availableToPurchaseInStore(
    storeResult: StoreLayerStoreResult,
    quantity: string | number,
  ): boolean {
    const messageFlags = this.findBasicMessageFlags(storeResult);
    const isSkuInStore = messageFlags.skusInStores;

    if (
      !messageFlags.skuSelected ||
      messageFlags.isSkuFound === undefined ||
      !messageFlags.doAllSelectedSkusHaveInventory ||
      isSkuInStore?.[0] === undefined
    ) {
      return false;
    }
    const isaInventory =
      this.searchLocation === SEARCH_LOCATION.BUNDLE
        ? this.lowestStoreResultISA[storeResult.store.location]
        : this.utilityService.convertToNumber(isSkuInStore[0].qty.isa);

    return !!(
      (this.crossChainBopis || messageFlags.foundSkus?.[0]?.isaFlag) &&
      isaInventory > 0 &&
      isaInventory >= this.utilityService.convertToNumber(quantity)
    );
  }

  /**
   * @description
   * Determines some basic flags used find store layer messaging
   * @param storeResult
   */
  public findBasicMessageFlags(storeResult: StoreLayerStoreResult): IStoreLayerMessage {
    let skuSelected = true;
    let isSkuFound = true;
    let doAllSelectedSkusHaveInventory = true;

    const selectedSku = this.storeService.isSkuSelected(this.storeLayerItems);
    if (!selectedSku) {
      skuSelected = false;
    }
    const found = this.storeService.findSelectedSku(this.storeLayerItems);
    if (found === undefined) {
      isSkuFound = false;
    }

    // only bundle will have multi selected products
    if (
      this.searchLocation === SEARCH_LOCATION.BUNDLE &&
      !this.doAllSelectedSkusHaveStoreInventory(storeResult)
    ) {
      doAllSelectedSkusHaveInventory = false;
    }
    const skuIds = this.storeService.convertDataIntoList(this.storeLayerItems);
    const isSkuInStore = this.storeService.isSkuInStore(
      'skus' in storeResult ? storeResult.skus : undefined,
      skuIds,
    );
    return {
      skuSelected,
      isSkuFound,
      doAllSelectedSkusHaveInventory,
      skusInStores: isSkuInStore,
      foundSkus: found,
    };
  }

  /**
   * @description
   * Determines if quantity exceeds availablity
   * @param storeResult
   * @param quantity
   */
  public qtyExceedsStoreAvailability(
    storeResult: StoreLayerStoreResult,
    quantity: string | number,
  ): boolean {
    const selectedSku = this.storeService.isSkuSelected(this.storeLayerItems);

    if (!selectedSku) {
      return false;
    }

    const found = this.storeService.findSelectedSku(this.storeLayerItems);

    if (found === undefined) {
      return false;
    }

    const storeSku = this.storeService.isSkuInStore(
      'skus' in storeResult ? storeResult.skus : undefined,
      [found[0].id],
    );

    if (storeSku === undefined || storeSku[0] === undefined) {
      return false;
    }

    const isaInventory =
      this.searchLocation === SEARCH_LOCATION.BUNDLE
        ? this.lowestStoreResultISA[storeResult.store.location]
        : this.utilityService.convertToNumber(storeSku[0].qty.isa);
    const boplInventory = this.utilityService.convertToNumber(storeSku[0].qty.bopl);

    const doesQtyExceedISA =
      isaInventory > 0 && isaInventory < this.utilityService.convertToNumber(quantity);
    const doesQtyExceedBOPL =
      boplInventory > 0 &&
      boplInventory < this.utilityService.convertToNumber(quantity) &&
      this.boplExperienceEnabled;

    return ((this.crossChainBopis || found[0]?.isaFlag) && doesQtyExceedISA) || doesQtyExceedBOPL;
  }

  public isStoreOpen(storeStatus: StoreStatus) {
    return this.storeService.isStoreStatusOpen(storeStatus);
  }

  public isStoreOpenForBopisOnly(storeStatus: StoreStatus) {
    return this.storeService.isStoreOpenForBopisOnly(storeStatus);
  }

  // // USE THIS IN REPLACE OF sku.id
  get getSkuIdentifier(): string | undefined {
    if (this.storeLayerItems === undefined) {
      return undefined;
    }
    // no items yet return an empty string
    if (this.storeLayerItems.length === 0) {
      return '';
    }
    // if store items length is greater than 1
    // set the string and return it
    if (this.storeLayerItems.length > 1) {
      this.skusIdentifier(this.storeLayerItems);
      return this.skusArrayIdString;
    }

    if (this.storeLayerItems[0].id === undefined) {
      return undefined;
    }
    if (this.storeLayerItems[0].id !== undefined) {
      return this.storeLayerItems[0].id;
    }
    return undefined;
  }

  /**
   * @description
   * creates a csv for list of store laysers
   * @param skus
   */
  skusIdentifier(skus: StoreLayerItem[]) {
    if (skus === undefined) {
      this.skusArrayIdString = '';
      return;
    }
    if (skus.length === 0) {
      this.skusArrayIdString = '';
      return;
    }
    if (skus.length >= 1) {
      this.skusArrayIdString = skus.map((sku) => sku.id).join(',');
    } else {
      this.skusArrayIdString = '';
    }
  }

  public isSkuBuyable(storeResult: StoreLayerStoreResult) {
    const selectedSku = this.storeService.isSkuSelected(this.storeLayerItems);

    if (!selectedSku) {
      return false;
    }
    // can i remove this??
    const found = this.storeService.findSelectedSku(this.storeLayerItems);
    if (found === undefined) {
      return false;
    }

    const storeSku = this.storeService.isSkuInStore(
      'skus' in storeResult ? storeResult.skus : undefined,
      [found[0].id],
    );

    if (storeSku === undefined || storeSku[0] === undefined) {
      return false;
    }

    const isaInventory =
      this.searchLocation === SEARCH_LOCATION.BUNDLE
        ? this.lowestStoreResultISA[storeResult.store.location]
        : this.utilityService.convertToNumber(storeSku[0].qty.isa);
    const atsInventory =
      this.searchLocation === SEARCH_LOCATION.BUNDLE
        ? this.lowestStoreResultATS[storeResult.store.location]
        : this.utilityService.convertToNumber(storeSku[0].qty.ats);

    // if any ats inventory return true
    if (atsInventory > 0) {
      return true;
    }
    // if we only have isa inventory then in store purchase only
    return !(isaInventory > 0 && atsInventory === 0);
  }

  public doAllSelectedSkusHaveStoreInventory(storeResult: StoreLayerStoreResult): boolean {
    // find all sku ids
    const skuIds = this.storeService.convertDataIntoList(this.storeLayerItems);
    // find all skus in the stores
    const isSkuInStore = this.storeService.isSkuInStore(
      'skus' in storeResult ? storeResult.skus : undefined,
      skuIds,
    );
    // make sure all stores have some sort of positive inventory
    const selectedSkusWithInvntory = this.checkForAnyInventory(isSkuInStore);

    // these 2 list must equal
    return selectedSkusWithInvntory.length === skuIds.length;
  }

  // Display store layer message functions
  public displayNotAvailableMessageAndStoreOpen(storeResult: StoreLayerStoreResult, quantity) {
    return (
      this.isStoreOpen(storeResult.store.status) &&
      !this.availableToPurchaseInStore(storeResult, quantity) &&
      !this.canPickupCurbside(storeResult, quantity) &&
      !this.qtyExceedsStoreAvailability(storeResult, quantity) &&
      !this.canShipToStore(storeResult, quantity)
    );
  }

  public displayNotAvailableMessageAndClosedToFootTraffic(storeResult: StoreLayerStoreResult) {
    return (
      this.storeService.isStoreStatusThree(storeResult.store.status) &&
      !this.isSkuBuyable(storeResult)
    );
  }

  public displayClosedToFootTraffic(storeResult: StoreLayerStoreResult) {
    return (
      this.storeService.isStoreStatusThree(storeResult.store.status) &&
      this.isSkuBuyable(storeResult)
    );
  }

  public displayInStoreOnlyMessage(storeResult: StoreLayerStoreResult, quantity: string | number) {
    return (
      this.isStoreOpen(storeResult.store.status) &&
      !this.isSkuBuyable(storeResult) &&
      this.availableToPurchaseInStore(storeResult, quantity) &&
      !this.storeService.isStoreStatusThree(storeResult.store.status)
    );
  }

  public displayAvailableToPurchaseInStore(
    storeResult: StoreLayerStoreResult,
    quantity: string | number,
  ) {
    return (
      this.isStoreOpen(storeResult.store.status) &&
      this.availableToPurchaseInStore(storeResult, quantity) &&
      !this.storeService.isStoreStatusThree(storeResult.store.status)
    );
  }

  public displayStoreTemporarilyClosed(storeResult: StoreLayerStoreResult) {
    return (
      !this.isStoreOpen(storeResult.store.status) &&
      !this.storeService.isStoreStatusThree(storeResult.store.status)
    );
  }

  public displayNotAvailableInStore(storeResult: StoreLayerStoreResult, quantity: string | number) {
    return (
      this.isStoreOpen(storeResult.store.status) &&
      !this.availableToPurchaseInStore(storeResult, quantity) &&
      !(
        !this.availableToPurchaseInStore(storeResult, quantity) &&
        !this.canPickupCurbside(storeResult, quantity)
      ) &&
      !this.storeService.isStoreStatusThree(storeResult.store.status)
    );
  }

  public displayAvailableForPickup(storeResult: StoreLayerStoreResult, quantity: string | number) {
    return (
      (this.storeService.isStoreStatusThree(storeResult.store.status) ||
        this.isStoreOpen(storeResult.store.status)) &&
      this.canPickupCurbside(storeResult, quantity)
    );
  }

  public displayPickupLaterMessage(storeResult: StoreLayerStoreResult, quantity: string | number) {
    return (
      this.isStoreOpen(storeResult.store.status) &&
      this.canShipToStore(storeResult, quantity) &&
      this.isSkuBuyable(storeResult) &&
      !this.canPickupCurbside(storeResult, quantity)
    );
  }

  trackByFn(index: number, _item: any): number {
    return index;
  }
}
